/**
 * VoiceSessionBar — rendered in place of the composer input row while a voice
 * session is anything but idle. Shows the session status (listening /
 * speaking pulse), the rolling live transcript, tool activity, an elapsed
 * timer, and the End button. The error state renders mapped copy with a
 * role-aware CTA — `VOICE_CREDENTIAL_MISSING` deep-links to the credentials
 * page (org scope for admins; the personal tab otherwise, since a personal
 * xAI key resolves first in the backend's tier walk).
 */

import { useEffect, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { AudioLines, CircleStop, Keyboard, Mic, SendHorizontal, TriangleAlert, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { unlockAudioPlayback } from '../../utils/audio-theme';
import { routes } from '../../lib/routes';
import { useAuth } from '../../store/auth-context';
import {
  useVoiceSession,
  voiceSessionStore,
  type VoiceSessionError,
  type VoiceSessionState,
} from '../../voice/voice-session-store';

function errorCopy(
  error: VoiceSessionError,
  isAdmin: boolean,
): { message: string; cta?: { label: string; to: string } } {
  switch (error.code) {
    case 'VOICE_CREDENTIAL_MISSING':
      return isAdmin
        ? {
            message: 'No xAI API key is connected for this organization.',
            cta: { label: 'Connect an xAI key', to: `${routes.console.credentials}?scope=org` },
          }
        : {
            message:
              'No xAI API key is connected for this organization. Ask an org admin to connect one, or add a personal key.',
            cta: { label: 'Open credentials', to: routes.console.credentials },
          };
    case 'VOICE_DISABLED':
      // The server message distinguishes the deployment kill switch from the
      // agent flag — prefer it when present.
      return { message: error.message || "Voice isn't available for this agent." };
    case 'BUSY':
      return { message: 'Finish the current run first.' };
    case 'VOICE_UNSUPPORTED':
      return { message: "Voice isn't supported here." };
    case 'VOICE_PROVIDER_ERROR':
    case 'TIMEOUT':
      return { message: 'The voice provider could not start the session — try again.' };
    default:
      return { message: error.message || 'Voice session error.' };
  }
}

function statusLabel(session: VoiceSessionState): string {
  switch (session.status) {
    case 'requesting_mic':
      return 'Requesting microphone…';
    case 'connecting':
      return 'Connecting…';
    case 'stopping':
      return 'Ending…';
    case 'active':
      if (session.agentSpeaking) return 'Speaking…';
      return session.micless ? 'Voice replies on' : 'Listening…';
    default:
      return '';
  }
}

/** mm:ss elapsed since the session went active. */
function ElapsedTimer({ startedAt }: { startedAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, []);
  const total = Math.max(0, Math.floor((now - startedAt) / 1000));
  const mm = Math.floor(total / 60);
  const ss = String(total % 60).padStart(2, '0');
  return (
    <span className="font-mono text-xs tabular-nums text-muted-foreground">
      {mm}:{ss}
    </span>
  );
}

/**
 * Typed input during a live session (`voice_text` frame): the agent answers
 * in speech. Enter sends; the persisted turn echoes back through the normal
 * transcript events, so the field just clears on send.
 */
function VoiceTypedInput({ micless }: { micless?: boolean }) {
  const [value, setValue] = useState('');
  const send = () => {
    const text = value.trim();
    if (!text) return;
    // Enter/click is a gesture — re-attempt the Safari audio unlock so a
    // session whose graph was left suspended recovers on the next turn.
    unlockAudioPlayback();
    voiceSessionStore.sendText(text);
    setValue('');
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };
  return (
    <div className="mt-1 flex items-center gap-1">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={
          micless ? 'Type a message — the agent talks back' : 'Type instead — the agent still talks back'
        }
        aria-label="Type a message to the voice session"
        maxLength={4000}
        className="min-w-0 flex-1 rounded-lg border border-border/60 bg-background/40 px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary/50"
      />
      <button
        type="button"
        aria-label="Send typed message"
        onClick={send}
        disabled={!value.trim()}
        className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-primary disabled:text-muted-foreground/40 hover:bg-foreground/5"
      >
        <SendHorizontal className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function VoiceSessionBar({
  session: sessionOverride,
}: {
  /** Dev-showcase override (`/dev/voice-session-bar`) — production callers
   *  render against the live store. */
  session?: VoiceSessionState;
} = {}) {
  const liveSession = useVoiceSession();
  const session = sessionOverride ?? liveSession;
  const { user } = useAuth();
  if (session.status === 'idle') return null;

  if (session.status === 'error' && session.error) {
    const { message, cta } = errorCopy(session.error, user?.role === 'admin');
    return (
      <div className="flex min-h-16 items-center gap-3 px-2 py-3">
        <TriangleAlert className="h-5 w-5 shrink-0 text-amber-500" />
        <div className="min-w-0 flex-1 text-sm text-foreground">
          {message}
          {cta && (
            <>
              {' '}
              <Link
                to={cta.to}
                className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
                onClick={() => voiceSessionStore.dismissError()}
              >
                {cta.label}
              </Link>
            </>
          )}
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => voiceSessionStore.dismissError()}
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // The live transcript line: prefer the in-flight partial, fall back to the
  // last finalized turn so the line doesn't blank out between turns.
  const transcript =
    session.assistantPartial ||
    session.userPartial ||
    session.lastFinalTurn?.text ||
    '';
  const transcriptRole = session.assistantPartial
    ? 'assistant'
    : session.userPartial
      ? 'user'
      : session.lastFinalTurn?.role;

  // Listening pulse scales subtly with mic level; speaking swaps the icon.
  const pulseScale = 1 + Math.min(0.4, session.micLevel * 1.2);

  return (
    <div className="flex min-h-16 items-center gap-3 px-2 py-3">
      <div className="relative inline-flex size-9 shrink-0 items-center justify-center">
        {session.status === 'active' && (
          <span
            aria-hidden
            className={cn(
              'absolute inset-0 rounded-full transition-transform duration-150',
              session.agentSpeaking ? 'bg-primary/15' : 'bg-emerald-500/15',
            )}
            style={{ transform: `scale(${session.agentSpeaking ? 1.15 : pulseScale})` }}
          />
        )}
        {session.agentSpeaking ? (
          <AudioLines className="relative h-5 w-5 text-primary" />
        ) : session.micless ? (
          <Keyboard
            className={cn(
              'relative h-5 w-5',
              session.status === 'active' ? 'text-emerald-500' : 'text-muted-foreground',
            )}
          />
        ) : (
          <Mic
            className={cn(
              'relative h-5 w-5',
              session.status === 'active' ? 'text-emerald-500' : 'text-muted-foreground',
            )}
          />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-foreground">{statusLabel(session)}</span>
          {session.startedAt != null && <ElapsedTimer startedAt={session.startedAt} />}
        </div>
        {transcript ? (
          <p
            className={cn(
              'truncate text-xs',
              transcriptRole === 'user' ? 'text-muted-foreground italic' : 'text-muted-foreground',
            )}
          >
            {transcriptRole === 'user' ? `“${transcript}”` : transcript}
          </p>
        ) : (
          session.status === 'active' && (
            <p className="truncate text-xs text-muted-foreground/60">
              {session.micless ? 'Type below — the agent answers out loud.' : 'Start talking…'}
            </p>
          )
        )}
        {session.toolActivity && (
          <p className="truncate text-xs text-accent-subagent-fg">{session.toolActivity}</p>
        )}
        {session.status === 'active' && <VoiceTypedInput micless={session.micless} />}
      </div>

      <button
        type="button"
        aria-label="End voice session"
        title="End voice session"
        disabled={session.status === 'stopping'}
        onClick={() => voiceSessionStore.stop()}
        className={cn(
          'size-9 shrink-0 rounded-full',
          'inline-flex items-center justify-center',
          'bg-background text-primary shadow shadow-black/30',
          'border-2 border-primary/70 disabled:border-foreground/25',
          'hover:bg-background/80',
        )}
      >
        <CircleStop className="h-5 w-5" />
      </button>
    </div>
  );
}
